import { GraphData } from '../interfaces'
import { MapFactProps } from '../components/map-fact'

interface Timeline {
  year: number
  content: string
  data: GraphData[]
  sourceData: GraphData[]
  facts: MapFactProps[]
}

const popMax = 2.5
const demandMax = 338
const percentMax = 100

export const timelineContent: Timeline[] = [
  {
    year: 1960,
    content:
      'Perth relied on dams as our main water source, providing 88% of our supply.',
    data: [
      {
        title: 'Population',
        unit: 'million',
        value: 0.55,
        max: popMax,
        color: 'darkGrey'
      },
      {
        title: 'Average inflow into dams',
        value: 420,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      },
      {
        title: 'Water supplied',
        value: 100,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      }
    ],
    sourceData: [
      {
        title: 'Dams',
        value: 88,
        max: percentMax,
        unit: '%',
        color: 'blue'
      },
      {
        title: 'Groundwater',
        value: 12,
        max: percentMax,
        unit: '%',
        color: 'green'
      },
      {
        title: 'Desalination',
        value: 0,
        max: percentMax,
        unit: '%',
        color: 'pink'
      },
      {
        title: 'Groundwater replenishment',
        value: 0,
        max: percentMax,
        unit: '%',
        color: 'orange'
      }
    ],
    facts: [
      {
        aboveValue: `In the 1960s, the majority of Perth's water came from dams.`,
        position: 'top-left'
      },
      {
        aboveValue: `Larger gardens, less water efficient appliances and fittings meant we were using more water for our household needs.`,
        position: 'bottom-right'
      },
      {
        aboveValue: `Water use charges were based on a standard allowance per household, rather than the usage based charges we have today.`,
        position: 'top-right'
      },
      {
        aboveValue: `Streamflow is the amount of water entering our dams from our catchment areas where water is collected by the natural landscape.`,
        position: 'bottom-left'
      }
    ]
  },
  {
    year: 1980,
    content:
      'Warmer temperatures mean less rain and water flowing into our dams, leading to increased reliance on groundwater.',
    data: [
      {
        title: 'Population',
        unit: 'million',
        value: 1,
        max: popMax,
        color: 'darkGrey'
      },
      {
        title: 'Average inflow into dams',
        value: 238,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      },
      {
        title: 'Water supplied',
        value: 200,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      }
    ],
    sourceData: [
      {
        title: 'Dams',
        value: 65,
        max: percentMax,
        unit: '%',
        color: 'blue'
      },
      {
        title: 'Groundwater',
        value: 35,
        max: percentMax,
        unit: '%',
        color: 'green'
      },
      {
        title: 'Desalination',
        value: 0,
        max: percentMax,
        unit: '%',
        color: 'pink'
      },
      {
        title: 'Groundwater replenishment',
        value: 0,
        max: percentMax,
        unit: '%',
        color: 'orange'
      }
    ],
    facts: [
      {
        aboveValue: `In response to extremely low dam levels in 1977, garden sprinklers were banned in the summers of 1977 and 1978.`,
        position: 'top-left'
      },
      {
        aboveValue: `With reduced streamflow into our dams, groundwater played an increasing role in Perth's water supply.`,
        position: 'bottom-right'
      },
      {
        aboveValue: `We introduced charges based on volume of water used to help reduce water consumption.`,
        position: 'top-right'
      },
      {
        aboveValue: `Garden bores gained popularity as an alernative water source for garden irrigation.`,
        position: 'bottom-left'
      }
    ]
  },
  {
    year: 2007,
    content:
      'As the water supplied by dams continued to reduce, we invested in desalination to reduce reliance our reliance on dams and groundwater.',
    data: [
      {
        title: 'Population',
        unit: 'million',
        value: 1.5,
        max: popMax,
        color: 'darkGrey'
      },
      {
        title: 'Average inflow into dams',
        value: 137,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      },
      {
        title: 'Water supplied',
        value: 275,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      }
    ],
    sourceData: [
      {
        title: 'Dams',
        value: 32,
        max: percentMax,
        unit: '%',
        color: 'blue'
      },
      {
        title: 'Groundwater',
        value: 61,
        max: percentMax,
        unit: '%',
        color: 'green'
      },
      {
        title: 'Desalination',
        value: 7,
        max: percentMax,
        unit: '%',
        color: 'pink'
      },
      {
        title: 'Groundwater replenishment',
        value: 0,
        max: percentMax,
        unit: '%',
        color: 'orange'
      }
    ],
    facts: [
      {
        aboveValue: `Perth's first desalination plant opened in 2006, it now provides up to 18% of Perth's water.`,
        position: 'top-left'
      },
      {
        aboveValue: `By 2007, we were using 20% less water per capita thanks to water efficiency measures implemented since the early 2000s.`,
        position: 'bottom-right'
      },
      {
        aboveValue: `We implemeted the watering roster and winter Sprinkler Switch-off, to reduce our water use.`,
        position: 'top-right'
      },
      {
        aboveValue: `Our second desalination plant opened in 2011, it provides up to 30% of Perth's water. `,
        position: 'bottom-left'
      }
    ]
  },
  {
    year: 2025,
    content:
      'Climate-independent water sources, like desalination and groundwater replenishment, now make up 50% of our water supply.',
    data: [
      {
        title: 'Population',
        unit: 'million',
        value: 2.5,
        max: popMax,
        color: 'darkGrey'
      },
      {
        title: 'Average inflow into dams',
        value: 80.5,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      },
      {
        title: 'Water supplied',
        value: 337,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      }
    ],
    sourceData: [
      {
        title: 'Dams',
        value: 22,
        max: percentMax,
        unit: '%',
        color: 'blue'
      },
      {
        title: 'Groundwater',
        value: 34,
        max: percentMax,
        unit: '%',
        color: 'green'
      },
      {
        title: 'Desalination',
        value: 38,
        max: percentMax,
        unit: '%',
        color: 'pink'
      },
      {
        title: 'Groundwater replenishment',
        value: 6,
        max: percentMax,
        unit: '%',
        color: 'orange'
      }
    ],
    facts: [
      {
        aboveValue: `Our groundwater replenishment scheme, introduced in 2012, produces enough water to supply 100,000 households.`,
        position: 'top-left'
      },
      {
        aboveValue: `With less water flowing into our dams, they now also store groundwater and desalinated water.`,
        position: 'bottom-right'
      },
      {
        aboveValue: `Since 2001 we've invested more than $2.2 billion to develop climate independent sources for Perth.`,
        position: 'top-right'
      },
      {
        aboveValue: `Per capita water use has reduced by 18% over the past decade, resulting from ongoing water efficiency measures, changing behaviours and higher density housing.`,
        position: 'bottom-left'
      }
    ]
  },
  {
    year: 2035,
    content:
      'As our water resources come under more pressure due to climate change, we will work with government, industry and communities to use water efficiently and enhance water in the natural environment.',
    data: [
      {
        title: 'Population',
        unit: 'million',
        value: 3,
        max: popMax,
        color: 'darkGrey'
      },
      {
        title: 'Average inflow into dams',
        value: 40,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      },
      {
        title: 'Water supplied',
        value: 386,
        max: demandMax,
        unit: 'Billion litres',
        color: 'darkGrey'
      }
    ],
    sourceData: [
      {
        title: 'Dams',
        value: 3,
        max: percentMax,
        unit: '%',
        color: 'blue'
      },
      {
        title: 'Groundwater',
        value: 25,
        max: percentMax,
        unit: '%',
        color: 'green'
      },
      {
        title: 'Desalination',
        value: 65,
        max: percentMax,
        unit: '%',
        color: 'pink'
      },
      {
        title: 'Groundwater replenishment',
        value: 7,
        max: percentMax,
        unit: '%',
        color: 'orange'
      }
    ],
    facts: [
      {
        aboveValue: `Our plan includes reducing scheme water use by 12%, increasing the use of recycled water and developing more climate independent sources.`,
        position: 'top-left'
      },
      {
        aboveValue: `We will continue invest in programs to encourage water efficiency, to help  defer the need for new water sources as long as reasonably possible.`,
        position: 'bottom-right'
      },
      {
        aboveValue: `Groundwater is climate dependent, to protect it, we will  consider new source options to maintain Perth's supply.`,
        position: 'top-right'
      },
      {
        aboveValue: `Water supply planning isn't static. We continuously monitor Perth's needs, so that we are prepared to add new sources to meet demand.`,
        position: 'bottom-left'
      }
    ]
  }
]
